import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import deepmerge from 'deepmerge';
import { Box, Chip, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import moment from 'moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getDefaultPaginationQueryValues } from '../../../../../../core/utils/api_utils';
import { getOrdersNotificationData, getPodsNotificationData } from '../../../../../../core/utils/notification_utils';
import PaginationControls from '../../../../../common/pagination_controls';
import { getAllPodsAction, getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import { GET_ROUTE_CUSTOMER_POD_ORDER_NEW, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import { getOrdersAction, getOrdersState } from '../../../../../../features/order/get_orders';
import { getOrderStatus, getOrderStatusColor, getPaymentType } from '../../../../../common/utils/order_utils';
import Filters from './filters';
import OrderDialog from './order_dialog';
import ReturnOrderDialog from './return_order_dialog';
import { Button } from '../../../../../common/button';
import ChangeStatusDialog from './change_status_dialog';
import { OrderStatusEnum } from '../../../../../../core/enums/order/order';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    sort: ['createdDate', 'desc']
};

const TransactionsList = ({ customerId, tab }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams, customerId });

    const getOrders = useSelector(getOrdersState);
    const getAllPods = useSelector(getAllPodsState);

    const refreshList = useCallback(() => {
        dispatch(getOrdersAction(queryParams))
            .then(response => showErrorMessage(getOrdersNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, queryParams]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    useEffect(() => {
        dispatch(getAllPodsAction())
            .then(response => showErrorMessage(getPodsNotificationData(response)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    return (
        <Box>
            { getAllPods?.data && (
                <Box mb={ 4 }>
                    <Filters setQueryParams={ setQueryParams } queryParams={ queryParams }/>
                </Box>
            ) }

            <Stack direction="row" mb={ 4 }>
                <Button onClick={ () => navigate(GET_ROUTE_CUSTOMER_POD_ORDER_NEW(customerId), { state: { tab } }) }>
                    Dodaj zakupy stacjonarne
                </Button>
            </Stack>

            { getOrders?.loading && (
                <Box mb={ 2 }>
                    <LinearProgress/>
                </Box>
            ) }

            { getOrders?.data?.content && (
                <Box>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Punkt sprzedaży</TableCell>
                                    <TableCell align="center">Data</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center">Kwota całkowita</TableCell>
                                    <TableCell align="center">Sposób płatności</TableCell>
                                    <TableCell align="center">Punkty lojalnościowe</TableCell>
                                    <TableCell align="right" sx={ { minWidth: 140 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { getOrders?.data?.content?.map(order => (
                                    <TableRow key={ order?.id } hover>
                                        <TableCell sx={ { fontWeight: 600 } }>
                                            { order?.id }
                                        </TableCell>
                                        <TableCell>
                                            { getAllPods?.data?.map(pod => ({
                                                id: pod?.id,
                                                label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                                            }))
                                                .find(pod => pod?.id === order?.podId)?.label || 'Brak' }
                                        </TableCell>
                                        <TableCell align="center">
                                            { moment(order?.createdDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={ getOrderStatus(order?.statusName) }
                                                color={ getOrderStatusColor(order?.statusName) }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={ { fontWeight: 500 } }>
                                            { order?.totalPrice.toFixed(2) } { order?.currency }
                                        </TableCell>
                                        <TableCell align="center">
                                            { getPaymentType(order?.paymentType) }
                                        </TableCell>
                                        <TableCell align="center">
                                            { order?.loyaltyPoint }
                                        </TableCell>


                                        <TableCell align="right">
                                            <Stack direction="row" justifyContent="flex-end" alignItems="center"
                                                   spacing={ 1 }>
                                                <OrderDialog order={ order }/>

                                                { order?.canCreateReturn && (
                                                    <ReturnOrderDialog order={ order } refreshList={ refreshList }/>
                                                ) }

                                                { order?.statusName === OrderStatusEnum.WAITING_FOR_PAYMENT.value && (
                                                    <ChangeStatusDialog order={ order } refreshList={ refreshList }/>
                                                ) }
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={ 3 } display="flex" justifyContent="center">
                        <PaginationControls
                            currentPage={ getOrders?.data?.pageable?.pageNumber }
                            totalPages={ getOrders?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default TransactionsList;